import { action, computed, observable } from 'mobx';
import { format, addDays, differenceInDays, parseISO } from 'date-fns';
import { DateOption } from '@link/china-railway/services/types';

import { chinaRailway } from '@link/china-railway/services';
import {
  GetRailwayInfoResponse,
  GetRailwayInfoRequest,
  GetTrainListRequest,
  RailwayTrainInfo,
  OrderTicketRequest,
  CancelIssuingOrderRequest
} from '@link/china-railway/services/types';
import i18n from '@link/china-railway/i18n';
import { getDateFnsLocale } from '@link/china-railway/utils';

const STORAGE_KEYS = {
  BOOKING_ID: 'china_railway_booking_id',
  TOKEN: 'china_railway_token',
  TICKET_URL: 'china_railway_ticket_url'
} as const;

export class ChinaRailway {
  @observable accessor bookingInfo: GetRailwayInfoResponse = {};
  @observable accessor trainList: RailwayTrainInfo[] = [];
  @observable accessor bookingId: string =
    sessionStorage.getItem(STORAGE_KEYS.BOOKING_ID) || '';
  @observable accessor token: string =
    sessionStorage.getItem(STORAGE_KEYS.TOKEN) || '';
  @observable accessor ticketUrl: string =
    sessionStorage.getItem(STORAGE_KEYS.TICKET_URL) || '';
  @observable accessor warningMessages: string[] = [];

  @action
  getBookingData = async (body: GetRailwayInfoRequest) => {
    const bookingInfo = await chinaRailway.getBookingInfo(body);
    if (bookingInfo) {
      this.setBookingData(bookingInfo);
    }
  };

  @action
  getTrainList = async (body: GetTrainListRequest) => {
    const trainList = await chinaRailway.getAvailableTrains(body);
    if (trainList) {
      this.setTrainList(trainList.trainInfos || []);
    }
  };

  @action
  orderTicket = (body: OrderTicketRequest) => {
    return chinaRailway.order({ body });
  };

  @action
  setBookingId(id: string) {
    this.bookingId = id;
    sessionStorage.setItem(STORAGE_KEYS.BOOKING_ID, id);
  }

  @action
  setToken(token: string) {
    this.token = token;
    sessionStorage.setItem(STORAGE_KEYS.TOKEN, token);
  }

  @action
  setTicketUrl(url: string) {
    this.ticketUrl = url;
    sessionStorage.setItem(STORAGE_KEYS.TICKET_URL, url);
  }

  @action
  setBookingData(detail: GetRailwayInfoResponse) {
    this.bookingInfo = detail;
  }

  @action
  setTrainList(list: RailwayTrainInfo[]) {
    this.trainList = list;
  }

  @action
  cancelOrder = (body: CancelIssuingOrderRequest) => {
    return chinaRailway.cancelIssuingOrder({
      body
    });
  };

  /**
   * Date options for quick selection
   */
  @computed
  get dateOptions(): DateOption[] {
    const locale = getDateFnsLocale(i18n.language);
    const firstDate = this.bookingInfo.planInfo?.orderableDateFrom;
    const lastDate = this.bookingInfo.planInfo?.orderableDateTo;

    if (!firstDate || !lastDate) {
      return [];
    }

    const startDate = parseISO(firstDate);
    const endDate = parseISO(lastDate);
    const daysDiff = differenceInDays(endDate, startDate) + 1;

    return Array.from({ length: daysDiff }, (_, i) => {
      const date = addDays(startDate, i);
      return {
        value: format(date, 'yyyy-MM-dd'),
        label: format(date, 'MM-dd'),
        weekday: format(date, 'EEE', { locale })
      };
    });
  }

  /**
   * Pre-order date options
   */
  @computed
  get preDateOptions(): DateOption[] {
    const locale = getDateFnsLocale(i18n.language);
    const preFirstDate = this.bookingInfo.planInfo?.preOrderableDateFrom;
    const preLastDate = this.bookingInfo.planInfo?.preOrderableDateTo;

    if (!preFirstDate || !preLastDate) {
      return [];
    }

    const preStartDate = parseISO(preFirstDate);
    const preEndDate = parseISO(preLastDate);
    const preDaysDiff = differenceInDays(preEndDate, preStartDate) + 1;

    return Array.from({ length: preDaysDiff }, (_, i) => {
      const date = addDays(preStartDate, i);
      return {
        value: format(date, 'yyyy-MM-dd'),
        label: format(date, 'MM-dd'),
        weekday: format(date, 'EEE', { locale })
      };
    });
  }

  @computed
  get calendarPreDateOptions(): Date[] {
    return this.preDateOptions.map(option => parseISO(option.value));
  }
  @computed
  get calendarDateOptions(): Date[] {
    return this.dateOptions.map(option => parseISO(option.value));
  }
}

export default new ChinaRailway();
